import React, { useState } from 'react';
import { secondUnitForLine, secondQtyFromQty, qtyFromSecondQty, formatTwoUnitQty } from '../utils/unitConversion';

/**
 * "Show Two Units" second qty box (module `item_two_units`, 2026-10-10).
 *
 * Sits under a line's qty box. Shows the same quantity in the other unit of
 * the item's two-unit pair (e.g. 10 Ctn → "= 240 Mtr"). Typing here converts
 * back with the item's fixed conversion and calls onChange(newLineQty), so the
 * line's own qty (the "Rate per" unit) and its amount update as usual.
 *
 * Renders nothing when the module is off, the item has no pair, or the line
 * is a size (L × H) line.
 */
const TwoUnitQtyBox = ({ item, unitCode, quantity, onChange, disabled = false, hasSize = false }) => {
  const [draft, setDraft] = useState(null);
  const second = hasSize ? null : secondUnitForLine(item, unitCode);
  if (!second) return null;
  const shown = draft != null ? draft : formatTwoUnitQty(secondQtyFromQty(item, quantity, unitCode, second));
  // Same layout as the qty row above it: the box sits under the qty box and the
  // unit name under the unit picker. Plain text box (no spinner arrows) so the
  // whole number fits in the narrow qty column.
  return (
    <div
      style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}
      title={`Same quantity in ${second} (1 ${unitCode} = ${formatTwoUnitQty(secondQtyFromQty(item, 1, unitCode, second))} ${second})`}
    >
      <input
        type="text"
        inputMode="decimal"
        autoComplete="off"
        data-two-unit-qty={second}
        value={shown}
        disabled={disabled}
        onFocus={(e) => { setDraft(shown === '0' ? '' : shown); try { e.target.select(); } catch (_) { /* ignore */ } }}
        onChange={(e) => {
          const v = e.target.value.replace(/,/g, '');
          if (!/^\d*\.?\d*$/.test(v)) return; // numbers only
          setDraft(v);
          onChange(qtyFromSecondQty(item, v, unitCode, second));
        }}
        onBlur={() => setDraft(null)}
        style={{
          flex: 1, width: '100%', minWidth: 0, padding: '4px 4px', fontSize: 12, textAlign: 'right',
          border: '1px dashed #94A3B8', borderRadius: 4, boxSizing: 'border-box',
          background: disabled ? '#F1F5F9' : '#F8FAFC', color: '#0F172A',
        }}
      />
      <span style={{ flexShrink: 0, minWidth: 56, padding: '0 4px', fontSize: 12, color: '#475569', fontWeight: 600, whiteSpace: 'nowrap' }}>{second}</span>
    </div>
  );
};

/** Read-only "= 250 Mtr" under a saved line's qty (document view). */
export const TwoUnitNote = ({ line }) => {
  if (!line || !line.secondUnitCode || line.secondQuantity == null || line.hasSize) return null;
  return (
    <div style={{ fontSize: 11, color: '#64748B' }}>= {formatTwoUnitQty(line.secondQuantity)} {line.secondUnitCode}</div>
  );
};

export default TwoUnitQtyBox;
